/**
 * Pure operations over a `SaveDocument` (task 9.8's profile management, export
 * and import). No storage, no clock reading beyond the timestamps it is handed,
 * so every one of these is directly testable.
 */
import { DEFAULT_SETTINGS, type ShellSettings } from '../settings.js';
import { migrate, SaveTooNewError, SaveUnreadableError } from './migrations.js';
import {
  EMPTY_CAREER,
  SAVE_VERSION,
  type CareerState,
  type MissionProgress,
  type MissionResult,
  type SavedRun,
  type SaveDocument,
  type SaveProfile,
} from './types.js';

export function newId(): string {
  const { crypto } = globalThis;
  if (typeof crypto?.randomUUID === 'function') return crypto.randomUUID();
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

export function emptyProfile(name: string, createdAt: string, id = newId()): SaveProfile {
  return {
    id,
    name,
    createdAt,
    settings: DEFAULT_SETTINGS,
    progress: [],
    career: EMPTY_CAREER,
    onboarding: [],
    tutorialsCompleted: [],
    run: null,
  };
}

/** The document a device starts from: one profile, default settings, no runs. */
export function emptyDocument(createdAt: string, name = 'Player 1'): SaveDocument {
  const profile = emptyProfile(name, createdAt);
  return { saveVersion: SAVE_VERSION, activeProfileId: profile.id, profiles: [profile] };
}

function replaceProfile(doc: SaveDocument, next: SaveProfile): SaveDocument {
  return {
    ...doc,
    profiles: doc.profiles.map((profile) => (profile.id === next.id ? next : profile)),
  };
}

/**
 * Applies `change` to one profile.
 *
 * Returns the *same document* when the change is a no-op, which several of the
 * operations below deliberately are — buying a perk twice, equipping into a
 * full set, dismissing an onboarding prompt already dismissed. Identity matters
 * because the view store persists and re-renders on every document it is
 * handed, and a refusal should cost neither.
 */
export function updateProfile(
  doc: SaveDocument,
  profileId: string,
  change: (profile: SaveProfile) => SaveProfile,
): SaveDocument {
  const found = doc.profiles.find((profile) => profile.id === profileId);
  if (!found) return doc;
  const next = change(found);
  return next === found ? doc : replaceProfile(doc, next);
}

export function addProfile(doc: SaveDocument, name: string, createdAt: string): SaveDocument {
  const profile = emptyProfile(name, createdAt);
  return { ...doc, activeProfileId: profile.id, profiles: [...doc.profiles, profile] };
}

export function renameProfile(doc: SaveDocument, profileId: string, name: string): SaveDocument {
  return updateProfile(doc, profileId, (profile) => ({ ...profile, name }));
}

export function switchProfile(doc: SaveDocument, profileId: string): SaveDocument {
  return doc.profiles.some((profile) => profile.id === profileId)
    ? { ...doc, activeProfileId: profileId }
    : doc;
}

/**
 * Deleting the last profile leaves a fresh empty one rather than a document
 * with no profiles: every screen assumes an active profile exists, and the
 * player asked to erase their progress, not to break the application.
 */
export function deleteProfile(
  doc: SaveDocument,
  profileId: string,
  createdAt: string,
): SaveDocument {
  const remaining = doc.profiles.filter((profile) => profile.id !== profileId);
  if (remaining.length === 0) return emptyDocument(createdAt);
  const active = remaining.some((profile) => profile.id === doc.activeProfileId)
    ? doc.activeProfileId
    : (remaining[0]?.id ?? '');
  return { ...doc, activeProfileId: active, profiles: remaining };
}

export function setSettings(
  doc: SaveDocument,
  profileId: string,
  settings: ShellSettings,
): SaveDocument {
  return updateProfile(doc, profileId, (profile) => ({ ...profile, settings }));
}

/**
 * Sets or clears the profile's in-progress run. At most one: starting a second
 * mission abandons the first, which is why the title screen names what Continue
 * would resume before the player commits to anything else.
 *
 * Called after every state-changing action (task 13.2), so it is written to be
 * cheap on the path that matters: an identical run — which is what a repeated
 * write of the same state is — returns the same profile and the store's
 * coalescing drops the write.
 */
export function setRun(doc: SaveDocument, profileId: string, run: SavedRun | null): SaveDocument {
  return updateProfile(doc, profileId, (profile) =>
    profile.run === run ? profile : { ...profile, run },
  );
}

/**
 * Records a finished run and banks what it earned.
 *
 * Two rules, both from the `scoring-progression` spec:
 *
 *   - the *best* result at a difficulty stands, so a worse replay changes
 *     nothing — including a failed replay of a mission already cleared;
 *   - Corporate Experience is banked as the improvement on that best, never the
 *     whole award again, so the shortest mission in the campaign is not the
 *     most profitable way to spend an evening.
 *
 * A completion is never walked back: a higher-scoring *loss* — possible where a
 * package pays generous partial credit — keeps the clearance it is replacing.
 */
export function recordResult(
  doc: SaveDocument,
  profileId: string,
  missionId: string,
  difficultyProfileId: string,
  result: MissionResult,
): SaveDocument {
  return updateProfile(doc, profileId, (profile) => {
    const existing = profile.progress.find((entry) => entry.missionId === missionId);
    const previous = existing?.best[difficultyProfileId];
    if (previous && previous.score >= result.score) return profile;

    const kept: MissionResult = {
      ...result,
      completed: result.completed || previous?.completed === true,
    };
    const merged: MissionProgress = {
      missionId,
      best: { ...existing?.best, [difficultyProfileId]: kept },
    };
    const banked = Math.max(0, result.cxpAwarded - (previous?.cxpAwarded ?? 0));

    return {
      ...profile,
      career: { ...profile.career, cxp: profile.career.cxp + banked },
      progress: existing
        ? profile.progress.map((entry) => (entry.missionId === missionId ? merged : entry))
        : [...profile.progress, merged],
    };
  });
}

/**
 * Records that an onboarding prompt has been dismissed. Idempotent, because
 * "never repeated once dismissed" has to survive a double-tap as readily as a
 * reload.
 */
export function dismissOnboarding(
  doc: SaveDocument,
  profileId: string,
  promptId: string,
): SaveDocument {
  return updateProfile(doc, profileId, (profile) =>
    profile.onboarding.includes(promptId)
      ? profile
      : { ...profile, onboarding: [...profile.onboarding, promptId].sort() },
  );
}

/**
 * Records that a guided walkthrough was walked to its end.
 *
 * Deliberately *not* `recordResult`: a tutorial holds no progress record, banks
 * no Corporate Experience, counts toward no total and satisfies no unlock
 * (`scoring-progression` spec). All that is kept is that this player has been
 * taught, which is what retires the contextual onboarding cards and what
 * mission select reports.
 *
 * Idempotent, like `dismissOnboarding` and for the same reason: finishing a
 * walkthrough twice is a thing a player can do, and it must not grow the list.
 */
export function recordTutorialCompleted(
  doc: SaveDocument,
  profileId: string,
  missionId: string,
): SaveDocument {
  return updateProfile(doc, profileId, (profile) =>
    profile.tutorialsCompleted.includes(missionId)
      ? profile
      : { ...profile, tutorialsCompleted: [...profile.tutorialsCompleted, missionId].sort() },
  );
}

/**
 * Buys a perk with banked Corporate Experience.
 *
 * Refuses silently — by returning the document unchanged — when the balance
 * will not cover it or it is already owned. The career screen never offers
 * either, so reaching here is a bug rather than a player action, and spending
 * CXP the player does not have is the one outcome worth being certain of.
 */
export function purchasePerk(
  doc: SaveDocument,
  profileId: string,
  perkId: string,
  cost: number,
): SaveDocument {
  return updateProfile(doc, profileId, (profile) => {
    const career = profile.career;
    if (career.ownedPerkIds.includes(perkId)) return profile;
    if (career.cxp < cost) return profile;
    const next: CareerState = {
      ...career,
      cxp: career.cxp - cost,
      ownedPerkIds: [...career.ownedPerkIds, perkId].sort(),
    };
    return { ...profile, career: next };
  });
}

/**
 * Equips or unequips an owned perk, enforcing the catalogue's slot count.
 *
 * The limit is enforced here as well as in the interface because `equippedPerks`
 * in the engine throws on an over-full set: a save that got past this function
 * would fail to open a run rather than fail to equip a perk.
 */
export function setPerkEquipped(
  doc: SaveDocument,
  profileId: string,
  perkId: string,
  equipped: boolean,
  equipSlots: number,
): SaveDocument {
  return updateProfile(doc, profileId, (profile) => {
    const career = profile.career;
    if (!career.ownedPerkIds.includes(perkId)) return profile;
    const already = career.equippedPerkIds.includes(perkId);
    if (equipped === already) return profile;
    if (equipped && career.equippedPerkIds.length >= equipSlots) return profile;
    const equippedPerkIds = equipped
      ? [...career.equippedPerkIds, perkId].sort()
      : career.equippedPerkIds.filter((id) => id !== perkId);
    return { ...profile, career: { ...career, equippedPerkIds } };
  });
}

export interface ImportSummary {
  readonly profileCount: number;
  readonly profileNames: readonly string[];
  readonly missionsCompleted: number;
  /** Profiles in the file carrying a resumable run. */
  readonly runsInProgress: number;
  /** The save format the file was written at, before any migration. */
  readonly saveVersion: number;
  /** True when the file had to be brought forward to this build's format. */
  readonly migrated: boolean;
}

export class ImportError extends Error {}

/**
 * Every field a run needs before it can be handed to the engine.
 *
 * An imported run that fails this is *dropped* rather than rejecting the whole
 * file. Progress is the thing a player would be devastated to lose; one
 * unresumable run is an inconvenience, and refusing the import over it would
 * cost them everything else in the file as well.
 */
function usableRun(value: unknown): SavedRun | null {
  const run = value as Partial<SavedRun> | null;
  if (!run || typeof run !== 'object') return null;
  const strings = ['runKey', 'missionId', 'packId', 'packVersion', 'profileId', 'seed'] as const;
  if (strings.some((field) => typeof run[field] !== 'string' || run[field] === '')) return null;
  if (typeof run.state !== 'object' || run.state === null) return null;
  if (!Array.isArray(run.history)) return null;
  return run as SavedRun;
}

/**
 * Validates imported save data and returns it alongside a summary to show
 * *before* anything is overwritten (task 13.6).
 *
 * The same migration registry the store uses runs here, so a file exported from
 * an older build imports rather than being refused; a file from a *newer* build
 * is refused rather than partially interpreted.
 */
export function parseImport(text: string): { doc: SaveDocument; summary: ImportSummary } {
  let raw: unknown;
  try {
    raw = JSON.parse(text) as unknown;
  } catch {
    throw new ImportError('That file is not valid JSON.');
  }

  const version = (raw as { saveVersion?: unknown } | null)?.saveVersion;
  let migrated;
  try {
    migrated = migrate(raw);
  } catch (cause) {
    if (cause instanceof SaveTooNewError || cause instanceof SaveUnreadableError) {
      throw new ImportError(cause.message);
    }
    throw new ImportError('That file is not a Managing Up save.');
  }

  const profiles: SaveProfile[] = migrated.doc.profiles.map((profile, index) => {
    if (typeof profile.name !== 'string' || profile.name.length === 0) {
      throw new ImportError(`Save ${String(index + 1)} in that file has no name.`);
    }
    return { ...profile, run: usableRun(profile.run) };
  });

  const active = profiles.some((profile) => profile.id === migrated.doc.activeProfileId)
    ? migrated.doc.activeProfileId
    : (profiles[0]?.id ?? '');

  return {
    doc: { saveVersion: SAVE_VERSION, activeProfileId: active, profiles },
    summary: {
      profileCount: profiles.length,
      profileNames: profiles.map((profile) => profile.name),
      missionsCompleted: profiles.reduce(
        (total, profile) =>
          total +
          profile.progress.filter((entry) =>
            Object.values(entry.best).some((result) => result.completed),
          ).length,
        0,
      ),
      runsInProgress: profiles.filter((profile) => profile.run !== null).length,
      saveVersion: typeof version === 'number' ? version : 0,
      migrated: migrated.applied.length > 0,
    },
  };
}

export function exportText(doc: SaveDocument): string {
  return `${JSON.stringify(doc, null, 2)}\n`;
}
